import * as React from "react";
import { useState, useMemo } from "react";
import {
  Typography,
  Paper,
  Box,
  Modal,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ExpertFilter } from "./expertFilter";
import { ProductFilter } from "./productFilter";
import { ProductCategoryFilter } from "./productCategoryFilter";
import { BrandFilter } from "./brandFilter";
import { AspectRatioFilter } from "./aspectRatioFilter";
import { DemoFilter } from "./demoFilter";
import { ExtraFilter } from "./extraFilter";
import { ContentCodeFilter } from "./contentCodeFilter";
import { StoryFilter } from "./storyFilter";
import { PdpUrlFilter } from "./pdpUrlFilter";
import { TagFilter } from "./tagFilter";
import { isInternal, countActiveFilters, VIDEO_V2_FILTER_KEYS, getVideoContextExcludeKeys } from "../../../../utils";
import { useUser } from "../../../../hooks/useUser";
import { useSearchParams } from "react-router-dom";
import { pathHasSegment } from "./helper";

export default function VideoFilters({
  isStoryVideos,
  isExpertVideos,
  isProduct,
  products,
  videoProps,
  isStories,
  brands,
}: any) {
  const [open, setOpen] = useState(false);
  const { role } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeFiltersCount = useMemo(
    () =>
      countActiveFilters(searchParams, VIDEO_V2_FILTER_KEYS, {
        excludeKeys: getVideoContextExcludeKeys(window.location.pathname),
      }),
    [searchParams]
  );

  const clearFilters = () => {
    const payload = {} as any;

    const isStoryPage = pathHasSegment(window.location.pathname, "embeds");
    const embedId = searchParams.get("embed");
    if (isStoryPage && embedId) payload.embed = embedId;

    const isProductPage = pathHasSegment(window.location.pathname, "products");
    const productId = searchParams.get("productId");
    const brandId = searchParams.get("brandId");
    if (isProductPage) {
      if (productId) payload.productId = productId;
      if (brandId) payload.brandId = brandId;
    }

    const isExpertPage = pathHasSegment(window.location.pathname, "experts");
    const proId = searchParams.get("proId");
    if (isExpertPage && proId) payload.proId = proId;

    setSearchParams(payload);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        sx={{
          borderRadius: "12px",
          border: "1px solid var(--border)",
          bgcolor: "var(--surface)",
          color: "var(--text-2)",
          display: "flex",
          alignItems: "center",
          textTransform: "none",
          px: "16px",
        }}
        startIcon={<FilterListIcon />}
      >
        Filters
        {activeFiltersCount > 0 && (
          <span style={{ marginLeft: 8, fontWeight: 450, color: "var(--text-2)" }}>
            ({activeFiltersCount} Selected)
          </span>
        )}
      </Button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          component={Paper}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "calc(100% - 24px)", md: "90vw", lg: 1000 },
            maxWidth: 1000,
            maxHeight: { xs: "calc(100vh - 24px)", sm: "90vh" },
            bgcolor: "var(--surface)",
            borderRadius: "12px",
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
            padding: "2px",
            outline: "none",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ padding: { xs: "12px", sm: "16px" } }}
          >
            <Typography
              sx={{
                color: "var(--Base-1000, #000)",
                fontFamily: '"Circular Std", sans-serif',
                fontSize: "24px",
                fontWeight: 900,
                lineHeight: "132%",
              }}
            >
              Videos Filters
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <hr style={{ margin: "0px 16px", color: "var(--border)" }} />

          <Box
            sx={{
              flexGrow: 1,
              maxHeight: { xs: "calc(100vh - 200px)", sm: 500 },
              overflowY: "auto",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: "16px",
              padding: "16px",
            }}
          >
            {!isProduct && (
              <ProductFilter
                {...videoProps}
                creatable
                queryParams={"videos=true"}
                options={products}
              />
            )}
            {!isStories && (
              <>
                {!isProduct && <ProductCategoryFilter {...videoProps} />}
                {!isExpertVideos && (
                  <ExpertFilter
                    {...videoProps}
                    queryParams={"videos=true"}
                  />
                )}
                {!isStoryVideos && <StoryFilter {...videoProps} creatable />}
                {!isProduct && isInternal(role) && (
                  <BrandFilter
                    {...videoProps}
                    queryParams={"videos=true"}
                    options={brands}
                  />
                )}
                <AspectRatioFilter {...videoProps} />
                <ExtraFilter {...videoProps} />
                <ContentCodeFilter {...videoProps} />
                <PdpUrlFilter {...videoProps} />
                {isInternal(role) && <DemoFilter {...videoProps} />}
                {isInternal(role) && <TagFilter {...videoProps} />}
              </>
            )}
          </Box>

          <Box display="flex" justifyContent="flex-end" my={"16px"}>
            <Button
              onClick={clearFilters}
              variant="outlined"
              sx={{
                mr: 2,
                textTransform: "capitalize",
                color: "var(--accent-text)",
                borderColor: "var(--accent-text)",
                "&:hover": { bgcolor: "var(--tint)" },
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
